
import {
	ComponentPublicInstance,
	computed,
	defineComponent,
	onMounted,
	PropType,
	ref
} from 'vue'
import { createVuexHelpers } from 'vue2-helpers'
import Popup from '@/components/modals/Popup.vue'
import CloseBtn from '@/components/CloseBtn.vue'
import AppleIcon from '@/assets/images/ic_apple_pay.svg'
import GooglePayIcon from '@/assets/images/ic_google_pay.svg'
import { PAYMENT_GATEWAY } from '@/constants'
import {
	buildTicketingFakeCreditCard,
	TEST_TICKETING_CREDIT_CARD
} from '@/constantsTicketingFakePayment'
import {
	ENABLE_TICKETING_PAYMENT_TYPE_QUERY_BRIDGE,
	TICKETING_PAYMENT_TYPE_STORAGE_KEY
} from '@/ticketingPaymentTypeQueryBridge'
import { checkIOs, checkMacOS } from '@/utils/helpers'
import { CreditCard } from '@/types/entitiesTypes'
import { AddCreditCard } from '@/types/storeTypes'
import storage from '@/utils/storage.js'

const { setStorage } = storage
const SHOW_ALL_WALLETS_FOR_TESTING = false

type PopupInstance = ComponentPublicInstance & {
	hide: () => void
}

export default defineComponent({
	name: 'TicketingFakePaymentMethodPopup',

	components: {
		CloseBtn,
		Popup
	},

	props: {
		creditCard: {
			type: Object as PropType<CreditCard | Record<string, unknown>>,
			default: () => ({})
		}
	},

	emits: ['payment-method-changed'],

	setup(props, { emit }) {
		const { useActions } = createVuexHelpers()
		const {
			addCreditCard
		}: {
			addCreditCard: AddCreditCard
		} = useActions('payment', ['addCreditCard'])

		const popup = ref<PopupInstance | null>(null)
		const isIOSOrMAC = ref(false)
		const isAndroid = ref(false)
		const loading = ref(false)

		const paymentType = computed(() => {
			return (
				(props.creditCard as CreditCard)?.cardHolderName ||
				(props.creditCard as CreditCard)?.paymentGatewayType ||
				PAYMENT_GATEWAY.CREDIT_CARD
			)
		})

		const isGooglePaySelected = computed(
			() => paymentType.value === PAYMENT_GATEWAY.ANDROID_PAY_CARD
		)
		const isApplePaySelected = computed(
			() => paymentType.value === PAYMENT_GATEWAY.APPLE_PAY_CARD
		)
		const shouldShowApplePay = computed(
			() =>
				SHOW_ALL_WALLETS_FOR_TESTING ||
				isIOSOrMAC.value ||
				isApplePaySelected.value
		)
		const shouldShowGooglePay = computed(
			() =>
				SHOW_ALL_WALLETS_FOR_TESTING ||
				isAndroid.value ||
				isGooglePaySelected.value
		)
		const isCardSelected = computed(
			() => !isApplePaySelected.value && !isGooglePaySelected.value
		)

		const creditCardNumbers = computed(
			() =>
				(props.creditCard as CreditCard)?.lastFour ||
				TEST_TICKETING_CREDIT_CARD.lastFour
		)
		const creditCardType = computed(() => {
			return (
				(props.creditCard as CreditCard)?.cardType
					?.toLowerCase()
					?.replace(/[/\s]/g, '') || 'visa'
			)
		})

		const appleIcon = computed(() => AppleIcon)
		const googleIcon = computed(() => GooglePayIcon)

		onMounted(() => {
			if (typeof navigator === 'undefined') return

			isIOSOrMAC.value = checkIOs() || checkMacOS()
			isAndroid.value = !isIOSOrMAC.value
		})

		const hide = (): void => {
			popup.value?.hide()
		}

		const changePaymentMethod = async (
			paymentMethod: string
		): Promise<void> => {
			if (loading.value) return

			try {
				loading.value = true
				const creditCard = await addCreditCard(
					buildTicketingFakeCreditCard(paymentMethod)
				)

				if (creditCard?.id) {
					if (ENABLE_TICKETING_PAYMENT_TYPE_QUERY_BRIDGE) {
						setStorage(TICKETING_PAYMENT_TYPE_STORAGE_KEY, paymentMethod)
					}

					emit('payment-method-changed', creditCard.id)
				}
			} catch (e) {
			} finally {
				loading.value = false
				hide()
			}
		}

		return {
			popup,
			loading,
			isIOSOrMAC,
			isAndroid,
			creditCardNumbers,
			creditCardType,
			isCardSelected,
			isApplePaySelected,
			isGooglePaySelected,
			shouldShowApplePay,
			shouldShowGooglePay,
			appleIcon,
			googleIcon,
			PAYMENT_GATEWAY,
			changePaymentMethod,
			hide
		}
	}
})
